// Cataloghi del builder "Crea sfondo con IA": dati puri, nessun DOM.
// I frammenti `prompt` vengono composti da structuredPrompt.js.

export const MAX_ELEMENTS = 3;
export const CUSTOM_TEXT_MAX_LENGTH = 120;
export const CUSTOM_ELEMENT_KEY = 'custom';
export const SACRED_SCENE_KEY = 'sacred';

export const BUILDER_ESSENCES = [
  {
    key: 'plain',
    label: 'Sfondo bianco',
    // Nessuna immagine: lo swatch CSS mostra un foglio bianco pulito, senza la
    // cornice finta che confondeva (vedi renderCardAsset in builderModalHtml).
    swatch: 'plain',
  },
  {
    key: 'gradient',
    label: 'Sfumato',
    asset: '/assets/sidebar/ai/builder/essenze/gradient.webp',
  },
  {
    key: 'scene',
    label: 'Scena',
    asset: '/assets/sidebar/ai/builder/essenze/scene.webp',
  },
  {
    key: 'paper',
    label: 'Carta',
    asset: '/assets/sidebar/ai/builder/essenze/paper.webp',
  },
  {
    key: 'material',
    label: 'Materiali',
    asset: '/assets/sidebar/ai/builder/essenze/material.webp',
  },
];

export const GRADIENT_DIRECTIONS = [
  {
    key: 'edges',
    label: 'Dai bordi',
    arrow: '◎',
    prompt: 'più intenso ai bordi e che sfuma verso un centro quasi bianco',
    promptToTone:
      'con il colore che sfuma gradualmente dai bordi verso il centro',
    promptFromWhite:
      'con il bianco ai bordi che sfuma gradualmente verso il colore al centro',
  },
  {
    key: 'top',
    label: 'Dall’alto',
    arrow: '↓',
    prompt: 'più intenso in alto e che sfuma delicatamente scendendo',
    promptFromWhite:
      'con il bianco in alto che sfuma delicatamente verso il colore in basso',
  },
  {
    key: 'bottom',
    label: 'Dal basso',
    arrow: '↑',
    prompt: 'più intenso in basso e che sfuma delicatamente salendo',
    promptFromWhite:
      'con il bianco in basso che sfuma delicatamente verso il colore in alto',
  },
  {
    key: 'left',
    label: 'Da sinistra',
    arrow: '→',
    prompt: 'più intenso a sinistra e che sfuma delicatamente verso destra',
    promptFromWhite:
      'con il bianco a sinistra che sfuma delicatamente verso il colore a destra',
  },
  {
    key: 'right',
    label: 'Da destra',
    arrow: '←',
    prompt: 'più intenso a destra e che sfuma delicatamente verso sinistra',
    promptFromWhite:
      'con il bianco a destra che sfuma delicatamente verso il colore a sinistra',
  },
  {
    key: 'top-left',
    label: 'Da alto-sx',
    arrow: '↘',
    prompt:
      'più intenso nell’angolo superiore sinistro e che sfuma in diagonale verso l’angolo opposto',
    promptFromWhite:
      'con il bianco nell’angolo superiore sinistro che sfuma in diagonale verso il colore nell’angolo opposto',
  },
  {
    key: 'top-right',
    label: 'Da alto-dx',
    arrow: '↙',
    prompt:
      'più intenso nell’angolo superiore destro e che sfuma in diagonale verso l’angolo opposto',
    promptFromWhite:
      'con il bianco nell’angolo superiore destro che sfuma in diagonale verso il colore nell’angolo opposto',
  },
  {
    key: 'bottom-left',
    label: 'Da basso-sx',
    arrow: '↗',
    prompt:
      'più intenso nell’angolo inferiore sinistro e che sfuma in diagonale verso l’angolo opposto',
    promptFromWhite:
      'con il bianco nell’angolo inferiore sinistro che sfuma in diagonale verso il colore nell’angolo opposto',
  },
  {
    key: 'bottom-right',
    label: 'Da basso-dx',
    arrow: '↖',
    prompt:
      'più intenso nell’angolo inferiore destro e che sfuma in diagonale verso l’angolo opposto',
    promptFromWhite:
      'con il bianco nell’angolo inferiore destro che sfuma in diagonale verso il colore nell’angolo opposto',
  },
];

export const GRADIENT_SPREADS = [
  {
    key: 'minimal',
    label: 'Minima',
    prompt:
      'con il colore in una fascia molto sottile e il resto dello sfondo quasi del tutto chiaro',
    promptFromWhite:
      'con il bianco raccolto in una fascia molto sottile e il tono di arrivo esteso su quasi tutto lo sfondo',
  },
  {
    key: 'narrow',
    label: 'Sottile',
    prompt:
      'con il colore raccolto in una fascia sottile e il resto dello sfondo molto chiaro',
    promptFromWhite:
      'con il bianco raccolto in una fascia sottile e il tono di arrivo esteso su gran parte dello sfondo',
  },
  { key: 'medium', label: 'Media', prompt: '', promptFromWhite: '' },
  {
    key: 'wide',
    label: 'Ampia',
    prompt:
      'con il colore ampio e avvolgente che occupa gran parte dello sfondo',
    promptFromWhite:
      'con il bianco esteso su gran parte dello sfondo e il tono di arrivo raccolto verso la fine della transizione',
  },
  {
    key: 'full',
    label: 'Massima',
    prompt:
      'con il colore che pervade quasi tutto lo sfondo, lasciando appena un respiro chiaro al centro',
    promptFromWhite:
      'con il bianco che accompagna quasi tutta la superficie e il tono di arrivo appena accennato all’estremità della transizione',
  },
];

// Quanto i due estremi dello sfumato si distinguono tra loro. L'ampiezza
// (`GRADIENT_SPREADS`) governa invece quanta superficie occupa la partenza:
// le due leve
// restano separate, così si può avere una fascia larga ma appena accennata o
// una fascia stretta e più decisa. `medium` è la resa storica e non aggiunge
// testo al prompt, preservando i brief precedenti.
export const GRADIENT_INTENSITIES = [
  {
    key: 'light',
    label: 'Leggera',
    prompt:
      'con la componente cromatica appena accennata, molto chiara e a contrasto minimo tra i due toni',
  },
  { key: 'medium', label: 'Media', prompt: '' },
  {
    key: 'strong',
    label: 'Intensa',
    prompt:
      'con la componente cromatica ben riconoscibile e presente, mantenendo morbido e graduale il passaggio',
  },
];
export const SCENE_PLACEMENTS = [
  { key: 'full', label: 'Piena', icon: '▣', phrase: '' },
  {
    key: 'left',
    label: 'Fascia sinistra',
    icon: '◀',
    phrase: 'collocata lungo la fascia sinistra del manifesto',
    axis: 'width',
  },
  {
    key: 'right',
    label: 'Fascia destra',
    icon: '▶',
    phrase: 'collocata lungo la fascia destra del manifesto',
    axis: 'width',
  },
  {
    key: 'top',
    label: 'Parte alta',
    icon: '▲',
    phrase: 'collocata nella parte alta del manifesto',
    axis: 'height',
  },
  {
    key: 'bottom',
    label: 'Parte bassa',
    icon: '▼',
    phrase: 'collocata nella parte bassa del manifesto',
    axis: 'height',
  },
];

export const SCENE_EXTENTS = [
  {
    key: 'quarter',
    label: 'Un quarto',
    fraction: 0.25,
    phrase: 'circa un quarto',
  },
  { key: 'third', label: 'Un terzo', fraction: 0.34, phrase: 'circa un terzo' },
  { key: 'half', label: 'Metà', fraction: 0.5, phrase: 'circa la metà' },
  {
    key: 'two-thirds',
    label: 'Due terzi',
    fraction: 0.67,
    phrase: 'circa due terzi',
  },
  {
    key: 'three-quarters',
    label: 'Tre quarti',
    fraction: 0.75,
    phrase: 'circa tre quarti',
  },
];

export const SCENE_FADES = [
  {
    key: 'fade',
    label: 'Dissolve',
    prompt: 'e si dissolve dolcemente verso il fondo',
  },
  {
    key: 'sharp',
    label: 'Netta',
    prompt: 'con un passaggio netto verso il fondo',
  },
];

export const SACRED_FIGURES = [
  {
    key: 'holymary',
    label: 'Madonna',
    prompt:
      'una delicata raffigurazione della Madonna in preghiera avvolta da luce morbida',
    asset: '/assets/sidebar/ai/builder/figure/holymary.webp',
  },
  {
    key: 'christ',
    label: 'Cristo',
    prompt:
      'una raffigurazione serena di Cristo dalla luce dolce e accogliente',
    asset: '/assets/sidebar/ai/builder/figure/christ.webp',
  },
  {
    key: 'guardianangel',
    label: 'Angelo custode',
    prompt:
      'una raffigurazione di angelo custode dalle ali morbide, immersa in una luce delicata',
    asset: '/assets/sidebar/ai/builder/figure/guardianangel.webp',
  },
  {
    key: 'mercifuljesus',
    label: 'Gesù Misericordioso',
    prompt:
      'una raffigurazione di Gesù Misericordioso con delicati raggi di luce dal cuore',
    asset: '/assets/sidebar/ai/builder/figure/mercifuljesus.webp',
  },
  {
    key: 'padrepio',
    label: 'Padre Pio',
    prompt:
      'una raffigurazione serena di Padre Pio dallo sguardo dolce e paterno',
    asset: '/assets/sidebar/ai/builder/figure/padrepio.webp',
  },
  {
    key: 'sanjoseph',
    label: 'San Giuseppe',
    prompt:
      'una raffigurazione serena di San Giuseppe dal volto mite e protettivo',
    asset: '/assets/sidebar/ai/builder/figure/sanjoseph.webp',
  },
  {
    key: 'santarita',
    label: 'Santa Rita',
    prompt: 'una raffigurazione devota di Santa Rita con una rosa tra le mani',
    asset: '/assets/sidebar/ai/builder/figure/santarita.webp',
  },
  {
    key: 'sanfrancesco',
    label: 'San Francesco',
    prompt:
      'una raffigurazione umile di San Francesco in serena contemplazione',
    asset: '/assets/sidebar/ai/builder/figure/sanfrancesco.webp',
  },
];

export const SACRED_FRAMINGS = [
  { key: 'fullfigure', label: 'Figura intera', prompt: 'a figura intera' },
  { key: 'halfbust', label: 'Mezzo busto', prompt: 'a mezzo busto' },
  { key: 'face', label: 'Primo piano', prompt: 'in primo piano sul volto' },
];

export const SACRED_PERSPECTIVES = [
  { key: 'frontal', label: 'Frontale', prompt: 'in vista frontale e solenne' },
  { key: 'threequarter', label: 'Tre quarti', prompt: 'di tre quarti' },
  {
    key: 'frombelow',
    label: 'Dal basso',
    prompt: 'con prospettiva leggermente dal basso, imponente',
  },
];

export const SACRED_MOODS = [
  {
    key: 'serene',
    label: 'Serena',
    prompt: 'con espressione serena e accogliente',
  },
  {
    key: 'merciful',
    label: 'Misericordiosa',
    prompt: 'con espressione misericordiosa',
  },
  {
    key: 'praying',
    label: 'In preghiera',
    prompt: 'con le mani giunte in preghiera',
  },
  { key: 'blessing', label: 'Benedicente', prompt: 'nell’atto di benedire' },
];

// Scene full-bleed (migrate dai soggetti “pieni” del vecchio modale).
export const BUILDER_SCENES = [
  {
    key: 'sky',
    label: 'Cielo & nuvole',
    orientation: 'both',
    prompt: 'cielo sereno con nuvole morbide e vaporose',
    asset: '/assets/sidebar/ai/builder/scene/sky.webp',
  },
  {
    key: 'landscape',
    label: 'Paesaggio',
    orientation: 'both',
    prompt: 'paesaggio naturale delicato e sfocato sullo sfondo',
    asset: '/assets/sidebar/ai/builder/scene/landscape.webp',
  },
  {
    key: 'abstract',
    label: 'Astratto / minimale',
    orientation: 'both',
    prompt: 'sfondo astratto e minimale con sfumature morbide',
    asset: '/assets/sidebar/ai/builder/scene/abstract.webp',
  },
  {
    key: 'light',
    label: 'Luce & raggi',
    orientation: 'both',
    prompt: 'luce dorata diffusa con raggi soffusi',
    asset: '/assets/sidebar/ai/builder/scene/light.webp',
  },
  {
    key: 'stars',
    label: 'Cielo stellato',
    orientation: 'both',
    prompt: 'cielo stellato sereno e profondo, discreto ed elegante',
    asset: '/assets/sidebar/ai/builder/scene/stars.webp',
  },
  {
    key: 'sea',
    label: 'Mare al tramonto',
    orientation: 'horizontal',
    prompt: 'mare calmo al tramonto con riflessi dorati e ampio orizzonte',
    asset: '/assets/sidebar/ai/builder/scene/sea.webp',
  },
  {
    key: 'mountains',
    label: 'Montagne',
    orientation: 'horizontal',
    prompt: 'panorama di montagne serene avvolte da una luce delicata',
    asset: '/assets/sidebar/ai/builder/scene/mountains.webp',
  },
  {
    key: 'wheat',
    label: 'Campo di grano',
    orientation: 'horizontal',
    prompt:
      'distesa di grano dorato fino all’orizzonte nella luce del tramonto',
    asset: '/assets/sidebar/ai/builder/scene/wheat.webp',
  },
  {
    key: 'hills',
    label: 'Colline al mattino',
    orientation: 'horizontal',
    prompt: 'dolci colline nella luce delicata del mattino',
    asset: '/assets/sidebar/ai/builder/scene/hills.webp',
  },
  {
    key: 'lakedawn',
    label: 'Lago all’alba',
    orientation: 'horizontal',
    prompt: 'lago calmo all’alba con riflessi delicati e orizzonte ampio',
    asset: '/assets/sidebar/ai/builder/scene/lakedawn.webp',
  },
  {
    key: SACRED_SCENE_KEY,
    label: 'Immagine sacra',
    orientation: 'both',
    prompt: '',
    // Mosaico delle otto figure disponibili: la card racconta la scelta
    // annidata che si apre selezionando la scena. Il blueprint continua a
    // usare l'asset della figura scelta (sceneAssetOf), non questo.
    asset: '/assets/sidebar/ai/builder/scene/sacred.webp',
  },
  {
    key: 'lightcross',
    label: 'Croce di luce',
    orientation: 'both',
    prompt: 'una croce luminosa che emerge da un chiarore soffuso',
    asset: '/assets/sidebar/ai/builder/scene/lightcross.webp',
  },
];

export const BUILDER_TECHNIQUES = [
  {
    key: 'watercolor',
    label: 'Acquerello',
    prompt: 'resa ad acquerello con pennellate delicate',
    asset: '/assets/sidebar/ai/builder/tecniche/watercolor.webp',
  },
  {
    key: 'photo',
    label: 'Fotografico soft',
    prompt: 'resa fotografica morbida con leggera sfocatura e luce naturale',
    asset: '/assets/sidebar/ai/builder/tecniche/photo.webp',
  },
  {
    key: 'painting',
    label: 'Dipinto classico',
    prompt: 'resa pittorica classica dalle pennellate raffinate',
    asset: '/assets/sidebar/ai/builder/tecniche/painting.webp',
  },
  {
    key: 'minimal',
    label: 'Minimale',
    prompt: 'resa grafica minimale, pulita ed essenziale',
    asset: '/assets/sidebar/ai/builder/tecniche/minimal.webp',
  },
  {
    key: 'chalkpastel',
    label: 'Pastello gessoso',
    prompt: 'resa a pastello gessoso dai toni polverosi e delicati',
    asset: '/assets/sidebar/ai/builder/tecniche/chalkpastel.webp',
  },
  {
    key: 'engraving',
    label: 'Incisione delicata',
    prompt: 'resa a incisione delicata dal tratto fine e ordinato',
    asset: '/assets/sidebar/ai/builder/tecniche/engraving.webp',
  },
  {
    key: 'oil',
    label: 'Olio classico',
    prompt: 'resa a olio classico dalle velature morbide',
    asset: '/assets/sidebar/ai/builder/tecniche/oil.webp',
  },
  {
    key: 'stainedglass',
    label: 'Vetrata soffusa',
    prompt: 'resa a vetrata soffusa dai colori smorzati e luminosi',
    asset: '/assets/sidebar/ai/builder/tecniche/stainedglass.webp',
  },
];

// Il supporto su cui l’immagine è resa. Vale come 4ª essenza (il fondo è la
// carta stessa) e come leva opzionale sotto sfumato e scena: è un substrato,
// sta sotto le cose.
//
// Dodici, cioè tre righe piene di `.ai-bgb-cards4`: con nove o dieci l’ultima
// card resterebbe orfana a capo. Ordinate come si leggono nella griglia —
// morbide, poi fibrose, infine speciali o con un tono proprio.
export const BUILDER_PAPERS = [
  {
    key: 'cotton',
    label: 'Cotone',
    prompt: 'carta di cotone dalla grana morbida e uniforme',
    asset: '/assets/sidebar/ai/builder/carte/cotton.webp',
  },
  {
    key: 'tissue',
    label: 'Velina',
    prompt:
      'carta velina leggerissima, dalle fibre finissime e appena traslucida',
    asset: '/assets/sidebar/ai/builder/carte/tissue.webp',
  },
  {
    key: 'felt',
    label: 'Feltrata',
    prompt: 'carta feltrata dalla superficie morbida e lievemente irregolare',
    asset: '/assets/sidebar/ai/builder/carte/felt.webp',
  },
  {
    key: 'laid',
    label: 'Vergata',
    prompt: 'carta vergata dalle sottili righe parallele in filigrana',
    asset: '/assets/sidebar/ai/builder/carte/laid.webp',
  },
  {
    key: 'rice',
    label: 'Riso',
    prompt: 'carta di riso dalle fibre lunghe appena visibili',
    asset: '/assets/sidebar/ai/builder/carte/rice.webp',
  },
  {
    key: 'linen',
    label: 'Telata',
    prompt:
      'carta telata con trama incrociata fine e regolare, chiaramente simile al lino',
    asset: '/assets/sidebar/ai/builder/carte/linen.webp',
  },
  {
    key: 'rough',
    label: 'Ruvida',
    prompt: 'carta ruvida a grana grossa, da disegno',
    asset: '/assets/sidebar/ai/builder/carte/rough.webp',
  },
  {
    key: 'papyrus',
    label: 'Papiro',
    prompt:
      'papiro dalle fibre intrecciate sottili, chiaro e impallidito dal tempo',
    asset: '/assets/sidebar/ai/builder/carte/papyrus.webp',
  },
  {
    key: 'parchment',
    label: 'Pergamena',
    prompt: 'pergamena dal tono avorio caldo, appena screziata',
    asset: '/assets/sidebar/ai/builder/carte/parchment.webp',
  },
  {
    key: 'aged',
    label: 'Anticata',
    prompt: 'carta anticata dal tono ingiallito, appena macchiata dal tempo',
    asset: '/assets/sidebar/ai/builder/carte/aged.webp',
  },
  {
    key: 'recycled',
    label: 'Riciclata',
    prompt: 'carta riciclata dalle minute inclusioni scure sparse nell’impasto',
    asset: '/assets/sidebar/ai/builder/carte/recycled.webp',
  },
  {
    key: 'hammered',
    label: 'Martellata',
    prompt:
      'carta martellata con fitta goffratura a buccia d’arancia, composta da piccoli rilievi e incavi tondeggianti irregolari chiaramente visibili',
    asset: '/assets/sidebar/ai/builder/carte/hammered.webp',
  },
];

// Quanto si vede la carta. Vive solo nel prompt: il blueprint non prova a
// mostrarla, perché l’intensità che renderà gpt-image-2 non la sappiamo.
export const PAPER_GRAINS = [
  {
    key: 'faint',
    label: 'Appena percettibile',
    prompt:
      'con una microtexture estremamente sottile e a contrasto minimo, visibile soltanto osservando da vicino nelle aree uniformi',
  },
  {
    key: 'soft',
    label: 'Delicata',
    prompt:
      'con una texture chiaramente riconoscibile ma discreta e a basso contrasto, secondaria rispetto alla composizione',
  },
  {
    key: 'marked',
    label: 'Marcata',
    prompt:
      'con una texture molto evidente e tattile, a contrasto deciso: fibre, righe o trama del supporto sono nettamente distinguibili anche a colpo d’occhio',
  },
];

// Superfici nobili scelte per restare chiare, sobrie e compatibili con la
// leggibilità di un manifesto funebre. Sono materiali a pieno campo, non
// fotografie di lastre o oggetti: la guardia geometrica vive nel prompt
// strutturato, mentre qui resta la sola identità visiva di ciascuna materia.
export const BUILDER_MATERIALS = [
  {
    key: 'marble',
    label: 'Marmo chiaro',
    prompt:
      'marmo chiaro opaco, con sottili venature irregolari grigio perla a basso contrasto',
    asset: '/assets/sidebar/ai/builder/materiali/marble.webp',
  },
  {
    key: 'alabaster',
    label: 'Alabastro',
    prompt:
      'alabastro bianco latteo e traslucido, con nuvolature morbide color avorio chiarissimo e profondità luminosa',
    asset: '/assets/sidebar/ai/builder/materiali/alabaster.webp',
  },
  {
    key: 'nacre',
    label: 'Madreperla',
    prompt:
      'madreperla chiara dalla superficie satinata, con riflessi perlacei opalescenti appena iridescenti',
    asset: '/assets/sidebar/ai/builder/materiali/nacre.webp',
  },
  {
    key: 'onyx',
    label: 'Onice bianco',
    prompt:
      'onice bianco traslucido, con venature vaporose color crema chiarissimo e luce diffusa dall’interno',
    asset: '/assets/sidebar/ai/builder/materiali/onyx.webp',
  },
  {
    key: 'silk',
    label: 'Seta opaca',
    prompt:
      'seta opaca color avorio chiaro, tesa e perfettamente piana, con trama finissima e lucentezza morbida',
    asset: '/assets/sidebar/ai/builder/materiali/silk.webp',
  },
  {
    key: 'frosted-glass',
    label: 'Vetro satinato',
    prompt:
      'vetro satinato bianco, diffusamente traslucido, con granulosità finissima e luce morbida senza riflessi speculari',
    asset: '/assets/sidebar/ai/builder/materiali/frosted-glass.webp',
  },
];

// La progressione usa tre segnali osservabili anche dal modello: distanza di
// lettura, contrasto e presenza delle caratteristiche proprie del materiale.
// «Forte» rende la materia più riconoscibile, non più scura o invadente.
export const MATERIAL_INTENSITIES = [
  {
    key: 'light',
    label: 'Leggera',
    prompt:
      'con l’identità del materiale appena accennata: venature, trama, traslucenza o riflessi sono estremamente sottili, a contrasto minimo e visibili soltanto osservando da vicino',
  },
  {
    key: 'medium',
    label: 'Media',
    prompt:
      'con l’identità del materiale chiaramente riconoscibile ma discreta: venature, trama, traslucenza o riflessi restano a basso contrasto e secondari rispetto alla composizione',
  },
  {
    key: 'strong',
    label: 'Forte',
    prompt:
      'con l’identità del materiale nettamente evidente a colpo d’occhio: venature, trama, traslucenza o riflessi sono ben distinti e più presenti su tutta la superficie, con contrasto moderato ma senza zone scure o invadenti',
  },
];

// Ogni tecnica è definita anche dal supporto che la regge: l’acquerello è
// acquerello perché il cotone lo assorbe, l’incisione perché la vergata tiene
// il tratto. Le tre tecniche senza un supporto naturale ricadono sul cotone,
// che è il neutro. La mappa suggerisce soltanto: vedi setTechnique.
export const TECHNIQUE_PAPER_DEFAULTS = {
  watercolor: 'cotton',
  photo: 'cotton',
  painting: 'linen',
  minimal: 'cotton',
  chalkpastel: 'rough',
  engraving: 'laid',
  oil: 'linen',
  stainedglass: 'cotton',
};

export const BUILDER_INTENSITIES = [
  {
    key: 'pastel',
    label: 'Pastello',
    prompt: 'colori pastello, tenui e luminosi',
    asset: '/assets/sidebar/ai/builder/intensita/pastel.webp',
  },
  {
    key: 'natural',
    label: 'Naturale',
    prompt: 'colori naturali ed equilibrati',
    asset: '/assets/sidebar/ai/builder/intensita/natural.webp',
  },
  {
    key: 'deep',
    label: 'Profondo',
    prompt: 'colori profondi e avvolgenti',
    asset: '/assets/sidebar/ai/builder/intensita/deep.webp',
  },
];

export const ATMOSPHERES = [
  {
    key: 'neutral',
    label: 'Neutra',
    prompt: 'luce uniforme e discreta',
    short: 'luce neutra',
    asset: '/assets/sidebar/ai/builder/atmosfera/neutral.webp',
  },
  {
    key: 'dawn',
    label: 'Alba',
    prompt: 'nella luce tenue e rosata dell’alba',
    short: 'luce d’alba',
    asset: '/assets/sidebar/ai/builder/atmosfera/dawn.webp',
  },
  {
    key: 'golden',
    label: 'Dorata',
    prompt: 'nella luce dorata del tardo pomeriggio',
    short: 'luce dorata',
    asset: '/assets/sidebar/ai/builder/atmosfera/golden.webp',
  },
  {
    key: 'ethereal',
    label: 'Eterea',
    prompt: 'in una luminosità eterea e diffusa',
    short: 'luce eterea',
    asset: '/assets/sidebar/ai/builder/atmosfera/ethereal.webp',
  },
  {
    key: 'dusk',
    label: 'Crepuscolo',
    prompt: 'nella luce quieta del crepuscolo',
    short: 'luce del crepuscolo',
    asset: '/assets/sidebar/ai/builder/atmosfera/dusk.webp',
  },
];

// Palette migrate dal vecchio modale. `tint`/`tintSoft` pilotano il
// blueprint via CSS custom properties.
export const BUILDER_PALETTES = [
  {
    key: 'none',
    label: 'Nessuno / Bianco',
    swatch: 'sw-none',
    prompt: '',
    tint: '#e9e9e9',
    tintSoft: '#ffffff',
  },
  {
    key: 'ivory',
    label: 'Avorio & champagne',
    swatch: 'sw-avorio',
    prompt: 'toni avorio e champagne, luce calda e diffusa',
    tint: '#e7d8ba',
    tintSoft: '#f7f1e3',
    toneName: 'avorio caldo',
  },
  {
    key: 'azure',
    label: 'Azzurro sereno',
    swatch: 'sw-azzurro',
    prompt: 'tonalità azzurro tenue, atmosfera serena e luminosa',
    tint: '#9fcfe6',
    tintSoft: '#dceef8',
    toneName: 'azzurro sereno',
  },
  {
    key: 'sunset',
    label: 'Oro & tramonto',
    swatch: 'sw-tramonto',
    prompt: 'sfumature oro pallido e rosa del tramonto',
    tint: '#e79e86',
    tintSoft: '#f8dcb4',
    toneName: 'oro e rosa del tramonto',
  },
  {
    key: 'night',
    label: 'Blu notte',
    swatch: 'sw-notte',
    prompt: 'tonalità blu profondo notturno, sobrio ed elegante',
    tint: '#46598f',
    tintSoft: '#aab4d4',
    toneName: 'blu notte',
  },
  {
    key: 'pearl',
    label: 'Grigio perla',
    swatch: 'sw-perla',
    prompt: 'tonalità neutre grigio perla, minimale ed elegante',
    tint: '#cdced6',
    tintSoft: '#eeeef2',
    toneName: 'grigio perla',
  },
  {
    key: 'sage',
    label: 'Verde salvia',
    swatch: 'sw-salvia',
    prompt: 'toni verde salvia tenue, sereno e naturale',
    tint: '#a9bfa2',
    tintSoft: '#dde8d8',
    toneName: 'verde salvia',
  },
  {
    key: 'lavender',
    label: 'Lavanda',
    swatch: 'sw-lavanda',
    prompt: 'delicate tonalità lavanda, quiete e spirituali',
    tint: '#b6a8cf',
    tintSoft: '#e4dcf0',
    toneName: 'lavanda',
  },
  {
    key: 'rose',
    label: 'Rosa antico',
    swatch: 'sw-rosa',
    prompt: 'toni rosa antico tenui e affettuosi',
    tint: '#d8a7ab',
    tintSoft: '#f1dcdd',
    toneName: 'rosa antico',
  },
  {
    key: 'taupe',
    label: 'Tortora caldo',
    swatch: 'sw-tortora',
    prompt: 'neutri caldi color tortora, discreti ed eleganti',
    tint: '#c6b8a6',
    tintSoft: '#ece4d8',
    toneName: 'tortora caldo',
  },
  {
    key: 'gold',
    label: 'Oro tenue',
    swatch: 'sw-oro',
    prompt: 'delicate tonalità oro tenue, calde e luminose',
    tint: '#d9c48a',
    tintSoft: '#f2e9cb',
    toneName: 'oro tenue',
  },
];

export const WHITE_TONE = {
  key: 'white',
  label: 'Bianco',
  swatch: 'sw-none',
  tint: '#ffffff',
  toneName: 'bianco',
};

export const BUILDER_ELEMENT_CATEGORIES = [
  {
    key: 'sacred',
    label: 'Sacri',
    items: [
      {
        key: 'cross',
        label: 'Croce',
        prompt: 'una singola croce luminosa dalle linee sobrie ed essenziali',
      },
      {
        key: 'angel',
        label: 'Angelo',
        prompt: 'un angelo dalle ali morbide e distese',
      },
      {
        key: 'mary',
        label: 'Madonna',
        prompt: 'una raffigurazione delicata della Madonna',
      },
      {
        key: 'prayinghands',
        label: 'Mani in preghiera',
        prompt: 'un paio di mani giunte in preghiera, delicate e discrete',
      },
      {
        key: 'rosary',
        label: 'Rosario',
        prompt: 'un rosario dai grani minuti, disposto con morbidezza',
      },
    ],
  },
  {
    key: 'nature',
    label: 'Natura',
    items: [
      {
        key: 'doves',
        label: 'Colombe in volo',
        prompt: 'due colombe bianche in volo leggero',
      },
      {
        key: 'roses',
        label: 'Rose',
        prompt: 'un piccolo gruppo di rose dai petali morbidi',
      },
      {
        key: 'flowers',
        label: 'Fiori misti',
        prompt:
          'una piccola composizione ordinata di fiori delicati dai toni tenui',
      },
      {
        key: 'olive',
        label: 'Ramo d’ulivo',
        prompt: 'due delicati rami d’ulivo incrociati',
      },
      {
        key: 'butterflies',
        label: 'Farfalle',
        prompt: 'tre farfalle leggere in volo delicato',
      },
      {
        key: 'leaves',
        label: 'Foglie',
        prompt: 'poche foglie tenui disposte con leggerezza',
      },
    ],
  },
  {
    key: 'lightfx',
    label: 'Luce',
    items: [
      {
        key: 'candles',
        label: 'Candele',
        prompt: 'tre candele accese dalla fiamma calma e dalla luce calda',
      },
      {
        key: 'rays',
        label: 'Raggi di luce',
        prompt: 'un fascio di soffusi raggi di luce dorata',
      },
      {
        key: 'stars',
        label: 'Stelle',
        prompt: 'una manciata di piccole stelle luminose e discrete',
      },
    ],
  },
];

export const ELEMENT_FIGURE_OPTIONS = {
  angel: [
    {
      key: 'aspect',
      label: 'Aspetto',
      options: [
        {
          key: 'ethereal',
          label: 'Etereo',
          prompt: 'dall’aspetto etereo e luminoso, senza tratti marcati',
        },
        {
          key: 'feminine',
          label: 'Femminile',
          prompt: 'dall’aspetto femminile e gentile',
        },
        {
          key: 'masculine',
          label: 'Maschile',
          prompt: 'dall’aspetto maschile e solenne',
        },
        {
          key: 'child',
          label: 'Bambino',
          prompt: 'con le sembianze di un bambino',
        },
      ],
    },
    {
      key: 'posture',
      label: 'Postura',
      options: [
        { key: 'standing', label: 'In piedi', prompt: 'raccolto in piedi' },
        { key: 'flying', label: 'In volo', prompt: 'in volo lieve' },
        {
          key: 'kneeling',
          label: 'Inginocchiato',
          prompt: 'inginocchiato in raccoglimento',
        },
      ],
    },
  ],
  mary: [
    {
      key: 'attitude',
      label: 'Atteggiamento',
      options: [
        { key: 'praying', label: 'In preghiera', prompt: 'in preghiera' },
        {
          key: 'blessing',
          label: 'Benedicente',
          prompt: 'nell’atto di benedire',
        },
        {
          key: 'gaze',
          label: 'Sguardo dolce',
          prompt: 'dallo sguardo dolce e accogliente',
        },
      ],
    },
  ],
};

export const elementFigureLevers = (itemKey) =>
  ELEMENT_FIGURE_OPTIONS[itemKey] || null;

export const defaultElementOptions = (itemKey) => {
  const levers = elementFigureLevers(itemKey);
  if (!levers) {
    return null;
  }
  const options = {};
  for (const lever of levers) {
    options[lever.key] = lever.options[0].key;
  }
  return options;
};

export const BUILDER_ZONES = [
  {
    key: 'top-left',
    label: 'Angolo alto sx',
    short: 'alto sx',
    phrase: 'nell’angolo superiore sinistro',
    kind: 'corner',
    gridArea: 'tl',
  },
  {
    key: 'top',
    label: 'Fascia superiore',
    short: 'alto',
    phrase: 'lungo la fascia superiore',
    kind: 'band',
    gridArea: 'top',
  },
  {
    key: 'top-right',
    label: 'Angolo alto dx',
    short: 'alto dx',
    phrase: 'nell’angolo superiore destro',
    kind: 'corner',
    gridArea: 'tr',
  },
  {
    key: 'left',
    label: 'Bordo sinistro',
    short: 'sx',
    phrase: 'lungo il bordo sinistro',
    kind: 'band',
    gridArea: 'left',
  },
  {
    key: 'right',
    label: 'Bordo destro',
    short: 'dx',
    phrase: 'lungo il bordo destro',
    kind: 'band',
    gridArea: 'right',
  },
  {
    key: 'bottom-left',
    label: 'Angolo basso sx',
    short: 'basso sx',
    phrase: 'nell’angolo inferiore sinistro',
    kind: 'corner',
    gridArea: 'bl',
  },
  {
    key: 'bottom',
    label: 'Fascia inferiore',
    short: 'basso',
    phrase: 'lungo la fascia inferiore',
    kind: 'band',
    gridArea: 'bottom',
  },
  {
    key: 'bottom-right',
    label: 'Angolo basso dx',
    short: 'basso dx',
    phrase: 'nell’angolo inferiore destro',
    kind: 'corner',
    gridArea: 'br',
  },
];

export const ZONE_RING = [
  'top-left',
  'top',
  'top-right',
  'right',
  'bottom-right',
  'bottom',
  'bottom-left',
  'left',
];

export const MAX_ZONE_CELLS = 3;

export const normalizeCellRun = (cells) => {
  if (!Array.isArray(cells) || !cells.length || cells.length > MAX_ZONE_CELLS) {
    return null;
  }
  const unique = [...new Set(cells)];
  if (unique.length !== cells.length) {
    return null;
  }
  if (unique.some((cell) => !ZONE_RING.includes(cell))) {
    return null;
  }
  if (unique.length === 1) {
    return unique;
  }
  for (const startCell of unique) {
    const start = ZONE_RING.indexOf(startCell);
    const run = Array.from(
      { length: unique.length },
      (_, offset) => ZONE_RING[(start + offset) % ZONE_RING.length]
    );
    if (run.every((cell) => unique.includes(cell))) {
      return run;
    }
  }
  return null;
};

const singleZoneCombos = BUILDER_ZONES.map((zone) => ({
  key: zone.key,
  cells: [zone.key],
  phrase: zone.phrase,
  short: zone.short,
}));

const PAIR_COMBOS = [
  [
    'top-left+top',
    'lungo la metà sinistra della fascia superiore',
    'metà sx fascia alta',
  ],
  [
    'top+top-right',
    'lungo la metà destra della fascia superiore',
    'metà dx fascia alta',
  ],
  [
    'top-right+right',
    'lungo la metà superiore del bordo destro',
    'metà alta bordo dx',
  ],
  [
    'right+bottom-right',
    'lungo la metà inferiore del bordo destro',
    'metà bassa bordo dx',
  ],
  [
    'bottom-right+bottom',
    'lungo la metà destra della fascia inferiore',
    'metà dx fascia bassa',
  ],
  [
    'bottom+bottom-left',
    'lungo la metà sinistra della fascia inferiore',
    'metà sx fascia bassa',
  ],
  [
    'bottom-left+left',
    'lungo la metà inferiore del bordo sinistro',
    'metà bassa bordo sx',
  ],
  [
    'left+top-left',
    'lungo la metà superiore del bordo sinistro',
    'metà alta bordo sx',
  ],
];

const TRIPLE_COMBOS = [
  [
    'top-left+top+top-right',
    'lungo tutta la fascia superiore, da angolo ad angolo, entro una fascia orizzontale stretta che non invade l’area centrale',
    'fascia alta intera',
  ],
  [
    'top-right+right+bottom-right',
    'lungo tutto il bordo destro, dall’angolo superiore a quello inferiore, entro una fascia laterale stretta che non invade l’area centrale',
    'bordo dx intero',
  ],
  [
    'bottom-right+bottom+bottom-left',
    'lungo tutta la fascia inferiore, da angolo ad angolo, entro una fascia orizzontale stretta che non invade l’area centrale',
    'fascia bassa intera',
  ],
  [
    'bottom-left+left+top-left',
    'lungo tutto il bordo sinistro, dall’angolo superiore a quello inferiore, entro una fascia laterale stretta che non invade l’area centrale',
    'bordo sx intero',
  ],
  [
    'left+top-left+top',
    'ad arco sull’angolo superiore sinistro, tra bordo sinistro e fascia superiore',
    'arco alto sx',
  ],
  [
    'top+top-right+right',
    'ad arco sull’angolo superiore destro, tra fascia superiore e bordo destro',
    'arco alto dx',
  ],
  [
    'right+bottom-right+bottom',
    'ad arco sull’angolo inferiore destro, tra bordo destro e fascia inferiore',
    'arco basso dx',
  ],
  [
    'bottom+bottom-left+left',
    'ad arco sull’angolo inferiore sinistro, tra fascia inferiore e bordo sinistro',
    'arco basso sx',
  ],
];

const comboFrom = ([key, phrase, short]) => ({
  key,
  cells: key.split('+'),
  phrase,
  short,
});

export const ZONE_COMBOS = [
  ...singleZoneCombos,
  ...PAIR_COMBOS.map(comboFrom),
  ...TRIPLE_COMBOS.map(comboFrom),
];

export const findZoneCombo = (cells) => {
  const normalized = normalizeCellRun(cells);
  if (!normalized) {
    return null;
  }
  const key = normalized.join('+');
  return ZONE_COMBOS.find((combo) => combo.key === key) || null;
};

export const FRAME_STYLES = [
  { key: 'none', label: 'Nessuna', css: 'fr-none', prompt: '' },
  {
    key: 'thin',
    label: 'Filetto',
    css: 'fr-thin',
    prompt: 'un sottile filetto lineare in oro',
    asset: '/assets/sidebar/ai/builder/cornici/thin.webp',
  },
  {
    key: 'silver',
    label: 'Argento',
    css: 'fr-silver',
    prompt: 'una modanatura in argento brunito',
    asset: '/assets/sidebar/ai/builder/cornici/silver.webp',
  },
  {
    key: 'classic',
    label: 'Classica',
    css: 'fr-classic',
    prompt: 'una modanatura classica in oro',
    asset: '/assets/sidebar/ai/builder/cornici/classic.webp',
  },
  {
    key: 'neoclassic',
    label: 'Neoclassica',
    css: 'fr-neoclassic',
    prompt: 'una modanatura neoclassica in oro con dentelli',
    asset: '/assets/sidebar/ai/builder/cornici/neoclassic.webp',
  },
  {
    key: 'acanthus',
    label: 'Foglia d’acanto',
    css: 'fr-acanthus',
    prompt: 'un motivo di foglie d’acanto e alloro in oro',
    asset: '/assets/sidebar/ai/builder/cornici/acanthus.webp',
  },
  {
    key: 'rich',
    label: 'Barocca',
    css: 'fr-rich',
    prompt: 'un motivo barocco a volute in oro',
    asset: '/assets/sidebar/ai/builder/cornici/rich.webp',
  },
  {
    key: 'floral',
    label: 'Liberty',
    css: 'fr-floral',
    prompt: 'un motivo floreale in stile Liberty in oro',
    asset: '/assets/sidebar/ai/builder/cornici/floral.webp',
  },
  {
    key: 'deco',
    label: 'Art Déco',
    css: 'fr-deco',
    prompt: 'un motivo Art Déco geometrico in oro',
    asset: '/assets/sidebar/ai/builder/cornici/deco.webp',
  },
  {
    key: 'marble',
    label: 'Marmo',
    css: 'fr-marble',
    prompt: 'una fascia in marmo grigio chiaro venato',
    asset: '/assets/sidebar/ai/builder/cornici/marble.webp',
  },
  {
    key: 'wood',
    label: 'Legno',
    css: 'fr-wood',
    prompt: 'una fascia in legno scuro intagliato',
    asset: '/assets/sidebar/ai/builder/cornici/wood.webp',
  },
  // Stili «morbidi»: trattamenti pittorici dei bordi, non cornici geometriche.
  // structuredPrompt li descrive senza la parola «cornice» né «spessore
  // uniforme» (che, su questi, farebbero disegnare un filetto rettangolare).
  {
    key: 'vignette',
    soft: true,
    label: 'Sfumatura ai bordi',
    css: 'fr-vignette',
    prompt: 'una vignettatura morbida che scurisce dolcemente verso i bordi',
    asset: '/assets/sidebar/ai/builder/cornici/vignette.webp',
  },
  {
    key: 'wash',
    soft: true,
    label: 'Velatura acquerello',
    css: 'fr-wash',
    prompt: 'una velatura ad acquerello sfumata verso i bordi',
    asset: '/assets/sidebar/ai/builder/cornici/wash.webp',
  },
  {
    key: 'dissolve',
    soft: true,
    label: 'Bordo dissolto',
    css: 'fr-dissolve',
    prompt:
      'un bordo in cui lo sfondo sfuma dolcemente fino a dissolversi al margine',
    asset: '/assets/sidebar/ai/builder/cornici/dissolve.webp',
  },
  {
    key: 'mat',
    label: 'Passe-partout',
    css: 'fr-mat',
    prompt: 'un passe-partout fotografico pulito e uniforme',
    asset: '/assets/sidebar/ai/builder/cornici/mat.webp',
  },
  {
    key: 'photoline',
    label: 'Filo fotografico',
    css: 'fr-photoline',
    prompt: 'un sottile filo netto, come il bordo di una stampa fotografica',
    asset: '/assets/sidebar/ai/builder/cornici/photoline.webp',
  },
  {
    key: 'softshadow',
    soft: true,
    label: 'Ombra morbida',
    css: 'fr-softshadow',
    prompt:
      'una morbida ombreggiatura interna lungo i bordi, che stacca con delicatezza il contenuto dal foglio',
    asset: '/assets/sidebar/ai/builder/cornici/softshadow.webp',
  },
  { key: 'custom', label: 'Personalizzata', css: 'fr-custom', prompt: '' },
];

// Asse separato dallo stile: quanto è elaborata la resa della cornice scelta
// sopra. Le frasi iniziano con «con resa» per attaccarsi a qualunque stile
// senza accordi di genere (es. "una modanatura ... con resa ricca").
export const FRAME_RICHNESS = [
  {
    key: 'minimal',
    label: 'Minima',
    prompt: 'con resa ridotta all’essenziale, appena percettibile',
  },
  {
    key: 'sober',
    label: 'Sobria',
    prompt: 'con resa molto sobria e discreta, pulita',
  },
  { key: 'medium', label: 'Media', prompt: 'con resa moderata e ordinata' },
  {
    key: 'rich',
    label: 'Ricca',
    prompt: 'con resa ricca ed elaborata, dai decori curati',
  },
  {
    key: 'veryrich',
    label: 'Molto ricca',
    prompt: 'con resa molto ricca e sontuosa, dai decori fitti ed elaborati',
  },
];

export const FRAME_COVERAGES = [
  {
    key: 'full',
    label: 'Completa',
    prompt: 'lungo tutto il perimetro, solo ai bordi',
  },
  {
    key: 'top-bottom',
    label: 'Alto e basso',
    prompt: 'solo lungo i bordi superiore e inferiore',
  },
  {
    key: 'sides',
    label: 'Ai lati',
    prompt: 'solo lungo i bordi laterali, sinistro e destro',
  },
  {
    key: 'c-left',
    label: 'A C',
    prompt:
      'lungo i bordi superiore, inferiore e sinistro, aperta sul lato destro (a forma di C)',
  },
  {
    key: 'c-right',
    label: 'A C rovesciata',
    prompt:
      'lungo i bordi superiore, inferiore e destro, aperta sul lato sinistro (come una C rovesciata)',
  },
  {
    key: 'u',
    label: 'A U',
    prompt:
      'lungo i bordi sinistro, destro e inferiore, aperta in alto (a forma di U)',
  },
  {
    key: 'arch',
    label: 'Ad arco',
    prompt:
      'lungo i bordi sinistro, destro e superiore, aperta in basso (come un arco)',
  },
  {
    key: 'corners',
    label: 'Solo angoli',
    prompt: 'solo agli angoli del manifesto, come decori angolari',
  },
  { key: 'custom', label: 'Personalizzato', prompt: '' },
];

export const FRAME_RELATIONS = [
  {
    key: 'overlay',
    label: 'Cornice sopra al contenuto',
    prompt:
      'sui tratti coperti la cornice si sovrappone con delicatezza al fondo, che prosegue dietro di essa fino al limite esterno del disegno stabilito dai margini',
  },
  {
    key: 'contain',
    label: 'Contenuto dentro la cornice',
    prompt:
      'sui tratti coperti la cornice delimita il fondo: sfondo, colore, scena ed elementi si arrestano nettamente al suo bordo interno e non passano sotto la cornice; sugli eventuali tratti aperti proseguono fino al limite esterno del disegno stabilito dai margini',
  },
];

export const FRAME_MARGIN_LEVELS = [
  { key: 'none', label: 'Nessuno' },
  { key: 'minimal', label: 'Minimo' },
  { key: 'small', label: 'Piccolo' },
];

export const findByKey = (list, key) =>
  list.find((entry) => entry.key === key) || null;

// Classificazione condivisa da prompt, UI e blueprint. Gli stili `soft` sono
// trattamenti pittorici del fondo, non cornici geometriche: per loro la
// relazione overlay/contain non ha significato e resta soltanto uno stato
// inattivo, preservato per quando l'utente torna a una cornice vera.
export const frameStyleKind = (styleKey) => {
  const style = findByKey(FRAME_STYLES, styleKey);
  if (!style || style.key === 'none') {
    return 'none';
  }
  return style.soft ? 'soft' : 'geometric';
};

export const isSoftFrameStyle = (styleKey) =>
  frameStyleKind(styleKey) === 'soft';

export const isGeometricFrameStyle = (styleKey) =>
  frameStyleKind(styleKey) === 'geometric';

export const GRADIENT_TO_TONES = [
  WHITE_TONE,
  ...BUILDER_PALETTES.filter((palette) => palette.key !== 'none'),
];

export const findToTone = (key) => findByKey(GRADIENT_TO_TONES, key);

export const findElementItem = (itemKey) => {
  for (const category of BUILDER_ELEMENT_CATEGORIES) {
    const item = findByKey(category.items, itemKey);
    if (item) {
      return item;
    }
  }
  return null;
};

export const scenesForOrientation = (orientation) =>
  BUILDER_SCENES.filter(
    (scene) => scene.orientation === 'both' || scene.orientation === orientation
  );

export const findSceneFade = (fadeBool) =>
  findByKey(SCENE_FADES, fadeBool ? 'fade' : 'sharp');

export const findSacredFigure = (key) =>
  findByKey(SACRED_FIGURES, key) || SACRED_FIGURES[0];

// Grana sconosciuta o mancante (brief vecchio, chiave rimossa dal catalogo):
// ricade sulla delicata invece di restituire null, così chi la usa non deve
// gestire un terzo caso oltre a «presente» e «assente».
export const findPaperGrain = (key) =>
  findByKey(PAPER_GRAINS, key) || findByKey(PAPER_GRAINS, 'soft');

// Intensità mancante o non più riconosciuta: conserva una materia visibile ma
// discreta invece di perdere l’intera clausola nei brief salvati.
export const findMaterialIntensity = (key) =>
  findByKey(MATERIAL_INTENSITIES, key) ||
  findByKey(MATERIAL_INTENSITIES, 'medium');

export const paperForTechnique = (techniqueKey) =>
  TECHNIQUE_PAPER_DEFAULTS[techniqueKey] || 'cotton';
